import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useProjectActivity, useProjectMembers } from '@/hooks/use-projects';
import { useLinkedBoardsTaskStats } from '../hooks/use-linked-boards-task-stats';
import { useProjectTimeStats } from '@/hooks/use-time-entries';
import { useExtensionsStore } from '@/stores/extensions';
import { httpStatus } from '@/lib/http-status';
import { activityLineText } from '@/features/time-tracker/lib/reopen-window';
import type { ProjectDetail, ProjectActivityItem } from '@hygge/shared';
import { Activity, Clock, ListTodo, Users } from 'lucide-react';

interface DashboardTabProps {
  project: ProjectDetail;
}

export function DashboardTab({ project }: DashboardTabProps) {
  const formatName = useEmployeeNameFormatter();
  const { data: activityData, isLoading: isLoadingActivity } = useProjectActivity(project.id, 10);
  const { data: members } = useProjectMembers(project.id);
  // An extension is "not connected" only when the list says so. While the list
  // is still loading, or for a reader who may not read it (403), a module is
  // not called off that may be on: the figures are asked for, and their own
  // answer decides what the card says.
  const isTaskManagerEnabled = useExtensionsStore(
    (s) => s.isExtensionEnabled('task-manager') || !s.isLoaded || s.loadFailed,
  );
  const isTimeTrackerEnabled = useExtensionsStore(
    (s) => s.isExtensionEnabled('time-tracker') || !s.isLoaded || s.loadFailed,
  );
  // Task stats are a board's, asked for by the board id. This passed the
  // project id, which names no board: the card read zero for everybody, and once
  // board routes checked access it read "not connected" for anyone but a
  // super_admin. A project can have several boards (HW-983); the figures are
  // those of every linked board the reader can open, added up.
  const {
    boardIds,
    stats: taskStats,
    isLoadingBoards,
    isBoardsError,
    isStatsError: isTaskStatsError,
  } = useLinkedBoardsTaskStats(project.id, isTaskManagerEnabled);
  const { data: timeStats, error: timeStatsError } = useProjectTimeStats(
    isTimeTrackerEnabled ? project.id : undefined,
  );
  // Refused is not failed: a reader without the time permission is shown the
  // empty card, not told the figures broke.
  const isTimeStatsError = !!timeStatsError && httpStatus(timeStatsError) !== 403;

  const activities = activityData?.data ?? [];
  const projectManagers = (members ?? []).filter((m) => m.isManager && !m.removedAt);
  const pm0Name = projectManagers[0]
    ? formatName({ firstName: projectManagers[0].firstName, lastName: projectManagers[0].lastName })
    : '';
  const managerSubtitle =
    projectManagers.length === 0
      ? 'No manager assigned'
      : projectManagers.length === 1
        ? `Managed by ${pm0Name}`
        : `Managed by ${pm0Name} +${projectManagers.length - 1}`;

  const taskValue = taskStats ? String(taskStats.total) : '—';
  // "Not connected" is the extension being off. A project with no board the
  // caller can open is a different fact, and said so on the Progress tab too.
  const taskSubtitle = taskStats
    ? `${taskStats.byCategory.done} done · ${taskStats.completionPct}% complete`
    : !isTaskManagerEnabled
      ? 'Task Manager not connected'
      : isBoardsError || isTaskStatsError
        ? 'Could not be loaded'
        : !isLoadingBoards && boardIds.length === 0
          ? 'No project board to show'
          : '';
  const taskMuted = !taskStats;

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Team Members"
          value={String(project.memberCount)}
          icon={<Users className="h-4 w-4" />}
          subtitle={managerSubtitle}
        />
        <MetricCard
          title="Status"
          value={project.status === 'active' ? 'Active' : project.status === 'on_hold' ? 'On Hold' : 'Archived'}
          icon={<Activity className="h-4 w-4" />}
          subtitle={`Since ${new Date(project.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`}
        />
        <MetricCard
          title="Tasks"
          value={taskValue}
          icon={<ListTodo className="h-4 w-4" />}
          subtitle={taskSubtitle}
          muted={taskMuted}
        />
        <MetricCard
          title="Hours MTD"
          value={timeStats ? String(timeStats.hoursMtd) : '—'}
          icon={<Clock className="h-4 w-4" />}
          subtitle={timeStats
            ? `${timeStats.byMember.length} member${timeStats.byMember.length !== 1 ? 's' : ''} tracked`
            : !isTimeTrackerEnabled
              ? 'Time Tracker not connected'
              : isTimeStatsError
                ? 'Could not be loaded'
                : ''}
          muted={!timeStats}
        />
      </div>

      {/* Activity feed */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingActivity ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-7 w-7 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-3/4" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : activities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Activity className="h-6 w-6 text-muted-foreground mb-2" />
              <p className="text-xs text-muted-foreground">No activity yet</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Activity will appear here as the project evolves
              </p>
            </div>
          ) : (
            <div className="space-y-0">
              {activities.map((activity) => (
                <ActivityRow key={activity.id} activity={activity} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({
  title,
  value,
  icon,
  subtitle,
  muted,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  subtitle: string;
  muted?: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">{title}</span>
          <span className="text-muted-foreground/50">{icon}</span>
        </div>
        <p className={`text-2xl font-semibold ${muted ? 'text-muted-foreground/40' : 'text-foreground'}`}>
          {value}
        </p>
        <p className="text-[11px] text-muted-foreground mt-1 truncate">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

const activityColorMap: Record<string, string> = {
  'member.added': 'bg-emerald-500',
  'member.removed': 'bg-red-500',
  'project.updated': 'bg-blue-500',
  'task.created': 'bg-violet-500',
  'task.status_changed': 'bg-amber-500',
  'task.assigned': 'bg-cyan-500',
  'time.logged': 'bg-orange-500',
  // Someone else's hours changed or removed, or any in a closed month. [HW-975]
  'time.edited': 'bg-yellow-500',
  'time.deleted': 'bg-rose-500',
  // A closed period reopened for a member. [HW-963]
  'time.reopened': 'bg-teal-500',
};

function ActivityRow({ activity }: { activity: ProjectActivityItem }) {
  const formatName = useEmployeeNameFormatter();
  const dotColor = activityColorMap[activity.type] || 'bg-muted-foreground';

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      <div className="mt-1.5">
        <div className={`h-2 w-2 rounded-full ${dotColor}`} />
      </div>
      {/* One line: the text on the left, who and when on the right; on a
          narrow card the right part wraps below the text, still right-aligned.
          The journal's lines name whose entry, so the actor lives here. [HW-975] */}
      <div className="flex-1 min-w-0 flex flex-wrap items-start gap-x-3 gap-y-0.5">
        <p className="text-xs text-foreground min-w-0 flex-1 basis-40 break-words">{activityLineText(activity)}</p>
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {activity.employee && (
            <div className="flex items-center gap-1">
              <Avatar className="h-4 w-4">
                <AvatarImage src={activity.employee.avatarUrl || undefined} />
                <AvatarFallback className="text-[7px]">
                  {activity.employee.firstName[0]}{activity.employee.lastName[0]}
                </AvatarFallback>
              </Avatar>
              <span className="text-[11px] text-muted-foreground">
                {formatName({ firstName: activity.employee.firstName, lastName: activity.employee.lastName })}
              </span>
            </div>
          )}
          <span className="text-[11px] text-muted-foreground/60">
            {formatRelativeTime(activity.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
